import { cn } from "~/utils/helpers"
import { formatNumber } from "~/utils/formatting"
import {
  getAtRiskDescriptionShort,
  getUnfulfillableDescriptionShort,
  type PreorderTimingCategory,
} from "~/utils/unfulfillablePreorderUtils"
import InfoTooltip from "../InfoTooltip"

export type PreorderMatrixRow = {
  timing: PreorderTimingCategory
  label: string
  unfulfillable: number
  atRisk: number
  fulfillable: number
  total: number
}

export type PreorderMatrixTotals = {
  unfulfillable: number
  atRisk: number
  fulfillable: number
  total: number
}

type Props = {
  rows: PreorderMatrixRow[]
  totals: PreorderMatrixTotals
}

// Column background tints — subtle so numbers stay readable
const COL_BG = {
  unfulfillable: "bg-[var(--theme-badge-red-bg)]",
  atRisk: "bg-[var(--theme-badge-orange-bg)]",
  fulfillable: "bg-[var(--theme-badge-green-bg)]",
  total: "",
} as const

const OUTSIDE_ROW_CLASS = "opacity-60"

export default function PreorderMatrix({ rows, totals }: Props) {
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-base">
          <th className="text-left font-medium py-2 px-3 text-fg-base">Timing</th>
          <th
            className={cn("text-right font-medium py-2 px-3 text-icon-red", COL_BG.unfulfillable)}
          >
            <span className="inline-flex items-center gap-1">
              Unfulfillable
              <InfoTooltip
                title={getUnfulfillableDescriptionShort()}
                side="top"
                containerClassName="max-w-xs"
                className="h-3.5 w-3.5"
              />
            </span>
          </th>
          <th className={cn("text-right font-medium py-2 px-3 text-icon-orange", COL_BG.atRisk)}>
            <span className="inline-flex items-center gap-1">
              At-risk
              <InfoTooltip
                title={getAtRiskDescriptionShort()}
                side="top"
                containerClassName="max-w-xs"
                className="h-3.5 w-3.5"
              />
            </span>
          </th>
          <th
            className={cn("text-right font-medium py-2 px-3 text-icon-green", COL_BG.fulfillable)}
          >
            Fulfillable
          </th>
          <th className="text-right font-medium py-2 px-3 text-fg-subtle">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isOutside = row.timing === "outside"
          return (
            <tr
              key={row.timing}
              className={cn("border-b border-base/50", isOutside && OUTSIDE_ROW_CLASS)}
            >
              <td className="py-2 px-3 font-medium text-fg-base">{row.label}</td>
              <td
                className={cn(
                  "text-right py-2 px-3 tabular-nums",
                  COL_BG.unfulfillable,
                  row.unfulfillable > 0 ? "text-icon-red font-medium" : "text-fg-subtle",
                )}
              >
                {row.unfulfillable > 0 ? formatNumber(row.unfulfillable) : "—"}
              </td>
              <td
                className={cn(
                  "text-right py-2 px-3 tabular-nums",
                  COL_BG.atRisk,
                  row.atRisk > 0 ? "text-icon-orange font-medium" : "text-fg-subtle",
                )}
              >
                {row.atRisk > 0 ? formatNumber(row.atRisk) : "—"}
              </td>
              <td
                className={cn(
                  "text-right py-2 px-3 tabular-nums",
                  COL_BG.fulfillable,
                  "text-icon-green",
                )}
              >
                {formatNumber(row.fulfillable)}
              </td>
              <td className="text-right py-2 px-3 tabular-nums text-fg-subtle">
                {formatNumber(row.total)}
              </td>
            </tr>
          )
        })}
        <tr className="border-t-2 border-base font-semibold">
          <td className="py-2 px-3 text-fg-base">Total</td>
          <td
            className={cn(
              "text-right py-2 px-3 tabular-nums",
              COL_BG.unfulfillable,
              totals.unfulfillable > 0 ? "text-icon-red" : "text-fg-subtle",
            )}
          >
            {totals.unfulfillable > 0 ? formatNumber(totals.unfulfillable) : "—"}
          </td>
          <td
            className={cn(
              "text-right py-2 px-3 tabular-nums",
              COL_BG.atRisk,
              totals.atRisk > 0 ? "text-icon-orange" : "text-fg-subtle",
            )}
          >
            {totals.atRisk > 0 ? formatNumber(totals.atRisk) : "—"}
          </td>
          <td
            className={cn("text-right py-2 px-3 tabular-nums text-icon-green", COL_BG.fulfillable)}
          >
            {formatNumber(totals.fulfillable)}
          </td>
          <td className="text-right py-2 px-3 tabular-nums text-fg-subtle">
            {formatNumber(totals.total)}
          </td>
        </tr>
      </tbody>
    </table>
  )
}
